import { Link } from "react-router";
import employerBuilding from "../../assets/finance-guide/employer-building.png";
import billingRate from "../../assets/finance-guide/billing-rate.png";
import premiumInvoice from "../../assets/finance-guide/premium-invoice.png";
import claimsFunding from "../../assets/finance-guide/claims-funding.png";
import vendorInvoice from "../../assets/finance-guide/vendor-invoice.png";
import paymentOut from "../../assets/finance-guide/payment-out.png";
import "./FinanceGuide.scss";

const MACHINES = [
    {
        id: "bill",
        dir: "In",
        recipe: { to: "/finance/billing-rates", label: "Billing rates" },
        cycle: { to: "/finance/ops", label: "Billing cycle runs" },
        output: { to: "/finance/bills", label: "Bills" },
        why: "HRP prices members from the rate recipe. The cycle is what creates the invoice.",
    },
    {
        id: "fund",
        dir: "Out",
        recipe: { to: "/finance/payment-cycles", label: "Claim payables" },
        cycle: { to: "/finance/payment-cycles", label: "Payment cycle runs" },
        output: { to: "/finance/funding-requests", label: "Funding request" },
        why: "When the self-funded payment cycle runs, HRP creates the cash call for those claims.",
    },
];

const FLOWS = [
    {
        id: "in",
        dir: "In",
        title: "Employer premium",
        blurb: "What they owe us this month.",
        art: premiumInvoice,
        artAlt: "Premium invoice",
        note: "Bills are not typed in. The billing cycle creates them from the rates.",
        steps: [
            { to: "/finance/billing-rates", label: "Billing rates", hint: "PEPM recipe on the account" },
            { to: "/finance/ops", label: "Billing cycle runs", hint: "HRP generates the bills" },
            { to: "/finance/bills", label: "Bills", hint: "Invoices that cycle just created" },
            { to: "/finance/payments", label: "Premium payment", hint: "Employer paid the invoice" },
        ],
    },
    {
        id: "claims",
        dir: "Out",
        title: "Claim payables",
        blurb: "What we owe providers.",
        art: claimsFunding,
        artAlt: "Claims and funding",
        note: "Same machine as bills: the cycle creates the document. Paying providers is later.",
        steps: [
            { to: "/finance/payment-cycles", label: "Payment cycle runs", hint: "Self-funded schedule" },
            { to: "/finance/funding-requests", label: "Funding request", hint: "Cash call that cycle just created" },
            { to: "/finance/payment-cycles", label: "Pay providers", hint: "After the cash is in" },
        ],
    },
    {
        id: "vendors",
        dir: "Out",
        title: "Vendor invoices",
        blurb: "PBM and network suppliers.",
        art: vendorInvoice,
        artAlt: "Vendor invoice",
        note: "Usually no funding request — they already billed us.",
        steps: [
            { to: "/finance/inbound-invoices", label: "Inbound invoice", hint: "Vendor’s bill" },
            { to: "/finance/payment-cycles", label: "Payment cycle", hint: "Supplier payable" },
            { to: "/finance/inbound-invoices", label: "Pay vendor", hint: "Same cycle path" },
        ],
    },
];

const MIXUPS = [
    { a: "Bill", b: "Funding request", why: "Premium invoice vs cash call for claim IDs", img: premiumInvoice },
    { a: "Billing rate", b: "Payable", why: "What the employer owes vs what we owe a provider", img: billingRate },
    { a: "Payment cycle", b: "Billing cycle", why: "Runs → funding request vs runs → bills", img: paymentOut },
    { a: "Funding", b: "Paying", why: "Collecting cash vs sending the provider check", img: claimsFunding },
];

const FinanceGuide = () => {
    return (
        <div className="container finance-guide">
            <header className="finance-guide__hero">
                <div>
                    <p className="title">How money moves</p>
                    <p className="sub-title">
                        Three tracks, one Account. They run side by side — premium in does not wait on claims out.
                        In HRP, a cycle is the machine that creates the paperwork.
                    </p>
                </div>
            </header>

            <Link to="/finance/ops" className="finance-guide__hub">
                <img src={employerBuilding} alt="" />
                <div>
                    <span>Start here</span>
                    <strong>Pick the Account in Ops</strong>
                    <p>Readiness, bill runs, and payment jobs all hang off that employer. Then use the track you need.</p>
                </div>
            </Link>

            <section className="finance-guide__machines" aria-label="How HRP generates the paperwork">
                <h2>How HRP generates it</h2>
                <p>
                    Same pattern twice. Configure the recipe, run the cycle, read the document it
                    created.
                </p>
                <ul>
                    {MACHINES.map((row) => (
                        <li key={row.id} className={`finance-guide__machine finance-guide__machine--${row.id}`}>
                            <span className="finance-guide__dir">{row.dir}</span>
                            <div className="finance-guide__machine-chain">
                                <Link to={row.recipe.to}>{row.recipe.label}</Link>
                                <span aria-hidden="true">→</span>
                                <Link to={row.cycle.to} className="finance-guide__machine-cycle">
                                    {row.cycle.label}
                                </Link>
                                <span aria-hidden="true">→</span>
                                <Link to={row.output.to}>{row.output.label}</Link>
                            </div>
                            <p>{row.why}</p>
                        </li>
                    ))}
                </ul>
            </section>

            <div className="finance-guide__flows" role="list">
                {FLOWS.map((flow) => (
                    <article key={flow.id} className={`finance-guide__flow finance-guide__flow--${flow.id}`} role="listitem">
                        <header>
                            <span className="finance-guide__dir">{flow.dir}</span>
                            <img src={flow.art} alt={flow.artAlt} />
                            <h2>{flow.title}</h2>
                            <p>{flow.blurb}</p>
                        </header>
                        <ol>
                            {flow.steps.map((step, i) => (
                                <li key={`${step.label}-${i}`}>
                                    <Link to={step.to}>
                                        <em>{i + 1}</em>
                                        <span>
                                            <strong>{step.label}</strong>
                                            {step.hint}
                                        </span>
                                    </Link>
                                </li>
                            ))}
                        </ol>
                        <p className="finance-guide__note">{flow.note}</p>
                    </article>
                ))}
            </div>

            <section className="finance-guide__mix" aria-label="Easy to mix up">
                <h2>Easy to mix up</h2>
                <ul>
                    {MIXUPS.map((row) => (
                        <li key={row.a}>
                            <img src={row.img} alt="" />
                            <p>
                                <strong>{row.a}</strong>
                                <span>is not</span>
                                <strong>{row.b}</strong>
                                {row.why}
                            </p>
                        </li>
                    ))}
                </ul>
            </section>
        </div>
    );
};

export default FinanceGuide;
